import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {ReactNode} from 'react';

import {
  FONT_PROXIMA_13,
  FONT_PROXIMA_11,
  FONT_WEIGHT_PROXIMA_REGULAR,
  FONT_WEIGHT_PROXIMA_SEMIBOLD,
} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {TooltipPlacement, TooltipWrapper} from 'ui-library/bs/Tooltip';
import {
  BLUE,
  BLUE_LIGHTER,
  BLUE_POWDER,
  GRAY_DARK,
  GRAY_SEMIDARK,
  ORANGE,
  RED_LIGHT,
  PURPLE,
  PURPLE_LIGHT,
} from 'ui-library/Colors';
import BenchlingDuotoneIcon, {
  BenchlingDuotoneIconName,
  isBenchlingDuotoneIconName,
} from 'ui-library/icons/BenchlingDuotoneIcon';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

import {BASE_FAILED_SIDEBAR_ITEM_DATA, SIDEBAR_EMPTY_ICON, SIDEBAR_SPINNER_ICON} from './SidebarItemDatas';

/**
 * Returns the appropriate icon style class based on icon type and selection state.
 */
function getIconStyleClass(icon: BenchlingDuotoneIconName | BenchlingIconName, isSelected: boolean): string {
  if (icon === BASE_FAILED_SIDEBAR_ITEM_DATA.icon) {
    return css(sidebarItemStyles.errorIcon);
  }
  if (icon === SIDEBAR_EMPTY_ICON) {
    return css(sidebarItemStyles.warningIcon);
  }
  if (icon === 'sparkle') {
    return css(sidebarItemStyles.sparkleIcon);
  }
  return css(isSelected ? sidebarItemStyles.selectedMonotoneIcon : sidebarItemStyles.unselectedIcon);
}

/** @package */
export interface SidebarItemProps {
  icon: BenchlingDuotoneIconName | BenchlingIconName;
  isSelected: boolean;
  onSelect: () => void;
  title: ReactNode;
  isFlaggingRows: boolean;
  disabled?: boolean;
  inactive?: boolean;
  tooltipText?: string;
  subtitleText?: string;
  dataTestAttrsKey?: DataTestAttrs['key'];
  isCollapsible?: boolean;
  isExpanded?: boolean;
  onToggleExpanded?: () => void;
}

/** @package */
export default function SidebarItem(props: SidebarItemProps): JSX.Element {
  function handleCollapseToggleClick(event: React.MouseEvent): void {
    if (!props.onToggleExpanded) {
      return;
    }
    event.stopPropagation();
    props.onToggleExpanded();
  }

  return (
    <TooltipWrapper tooltipPlacement={TooltipPlacement.AUTO} tooltipTitle={props.tooltipText ?? null}>
      <div
        className={css(
          sidebarItemStyles.wrapper,
          props.isSelected && sidebarItemStyles.selectedSidebarItem,
          props.disabled === true && sidebarItemStyles.disabledSidebarItem,
          props.inactive === true && sidebarItemStyles.inactiveSidebarItem,
          props.isCollapsible && sidebarItemStyles.collapsibleItem
        )}
        onClick={() => {
          if (!props.disabled) {
            props.onSelect();
          }
        }}
        onDoubleClick={handleCollapseToggleClick}
        {...getTestProps({component: 'SidebarItem', element: 'root', key: props.dataTestAttrsKey})}
      >
        <div className={css(sidebarItemStyles.leftWrapper)}>
          {props.isCollapsible && (
            <div className={css(sidebarItemStyles.collapseToggle)} onClick={handleCollapseToggleClick}>
              <BenchlingIcon
                icon={props.isExpanded ? 'caret-down' : 'caret-right'}
                className={css(sidebarItemStyles.collapseToggleIcon)}
              />
            </div>
          )}
          <div className={css(sidebarItemStyles.iconWrapper)}>
            {props.isSelected && isBenchlingDuotoneIconName(props.icon) ? (
              <BenchlingDuotoneIcon
                icon={props.icon}
                primaryColor={BLUE}
                secondaryColor={BLUE_LIGHTER}
                spin={props.icon === SIDEBAR_SPINNER_ICON}
              />
            ) : (
              <BenchlingIcon
                icon={props.icon}
                className={classNames(
                  props.icon === SIDEBAR_SPINNER_ICON && 'fa-spin',
                  getIconStyleClass(props.icon, props.isSelected)
                )}
              />
            )}
          </div>
          {props.isFlaggingRows && (
            <div className={css(sidebarItemStyles.iconWrapper)}>
              <BenchlingIcon icon="flag" className={css(sidebarItemStyles.flagIcon)} />
            </div>
          )}
          <div className={css(sidebarItemStyles.titleWrapper)}>
            {props.title}
            {props.subtitleText && (
              <div className={css(sidebarItemStyles.subtitleText)}>{props.subtitleText}</div>
            )}
          </div>
        </div>
      </div>
    </TooltipWrapper>
  );
}

const COLLAPSIBLE_TOGGLE_WIDTH = 8; // px
const ITEM_LEFT_PADDING = 20; // px
const COLLAPSIBLE_TOGGLE_SPACING = (ITEM_LEFT_PADDING - COLLAPSIBLE_TOGGLE_WIDTH) / 2;

/** @package */
export const sidebarItemStyles = StyleSheet.create({
  wrapper: {
    ...FONT_PROXIMA_13,
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    marginTop: 3,
    padding: `7px 9px 7px ${ITEM_LEFT_PADDING}px`,
    borderRadius: 2,
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    ':hover': {backgroundColor: BLUE_POWDER},
  },
  leftWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
    wordBreak: 'break-word',
    width: '100%',
  },
  iconWrapper: {
    flexShrink: 0,
    marginRight: 8,
  },
  titleWrapper: {
    flexDirection: 'column',
    flexGrow: 1,
  },
  selectedSidebarItem: {
    backgroundColor: BLUE_POWDER,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  selectedMonotoneIcon: {
    color: BLUE,
  },
  unselectedIcon: {
    color: GRAY_DARK,
  },
  errorIcon: {
    color: RED_LIGHT,
  },
  warningIcon: {
    color: ORANGE,
  },
  disabledSidebarItem: {
    color: GRAY_DARK,
    cursor: 'default',
  },
  inactiveSidebarItem: {
    color: GRAY_DARK,
    fontStyle: 'italic',
  },
  subtitleText: {
    ...FONT_PROXIMA_11,
    color: GRAY_SEMIDARK,
  },
  flagIcon: {
    color: PURPLE,
  },
  sparkleIcon: {
    color: PURPLE_LIGHT,
  },
  collapsibleItem: {
    // To keep the icons and text of collapsible items aligned with non-collapsible items, we need to reduce
    // the padding to allow space for the caret and it's right right-margin.
    padding: `7px 9px 7px ${COLLAPSIBLE_TOGGLE_SPACING}px`,
  },
  collapseToggle: {
    marginRight: COLLAPSIBLE_TOGGLE_SPACING,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    // We need a constant width so that it has the same width wether we render the down and right caret
    width: COLLAPSIBLE_TOGGLE_WIDTH,
    cursor: 'pointer',
  },
  collapseToggleIcon: {
    color: GRAY_DARK,
    fontSize: 12,
  },
});
